import SearchInput from '@shared/components/SearchInput/SearchInput';
import { KnownMembership, RoomMember } from 'matrix-js-sdk/src/matrix';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Link } from 'assets/icons/Link.svg';
import { ReactComponent as UserPlus } from 'assets/icons/UserPlus.svg';
import InviteInputBase from 'components/InviteInput/InviteInputBase';
import Spinner from 'components/Spinner/Spinner';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { memberMatchesInviteSearch } from 'modules/VideoMeetings/ScheduleMeetingModal/memberMatchesInviteSearch';
import { getInviteableWorkspaceMembers } from 'modules/Workspaces/WorkspaceSettingsModal/MembersTab/MembersSection/utils';
import useWorkspaceMembers from 'modules/Workspaces/WorkspaceSettingsModal/MembersTab/useWorkspaceMembers';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getIsFromGuestServer, LoadingMembersStatusEnum } from 'modules/Workspaces/WorkspacesProvider/utils';

import InviteMembersFieldWrapper from './InviteMembersFieldWrapper';
import SuggestedMembersField from './SuggestedMembersField';
import { buildSuggestionsFromRooms } from './suggestedUsersScoring';
import useExistingRoomMemberIds from './useExistingRoomMemberIds';
import WorkspaceMembersField from './WorkspaceMembersField';

interface InviteMembersFieldProps {
  isPublic: boolean;
  invitees: string[];
  addInvitee: (invitee: string) => void;
  removeInvitee: (invitee: string) => void;
  setInvitees: (invitees: string[]) => void;
  errors?: Record<string, unknown>;
  onCopyLink?: () => void;
  excludeRoomId?: string;
}

const InviteMembersField = ({
  isPublic,
  invitees,
  addInvitee,
  removeInvitee,
  setInvitees,
  errors = {},
  onCopyLink,
  excludeRoomId,
}: InviteMembersFieldProps) => {
  const { workspaceId, loadingMembersStatus } = useWorkspacesContext();
  const { matrixClient } = useMatrixContext();
  const allWorkspaceMembers = useWorkspaceMembers();
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();

  const isLoadingWorkspaceMembers =
    workspaceId !== null &&
    [LoadingMembersStatusEnum.READY, LoadingMembersStatusEnum.LOADING].includes(loadingMembersStatus);

  const resetSearch = useCallback(() => setSearchValue(''), []);
  const { memberIds: existingRoomMemberIds, isReady: excludeRoomMembersReady } =
    useExistingRoomMemberIds(excludeRoomId);

  const suggestedMembers = useMemo(() => {
    if (workspaceId) return [];

    const rooms = matrixClient.getRooms().filter((room) => room.getMyMembership() === KnownMembership.Join);
    const currentUserId = matrixClient.getUserId();
    const excludedUserIds = currentUserId ? [currentUserId, ...existingRoomMemberIds] : [...existingRoomMemberIds];

    return buildSuggestionsFromRooms(rooms, excludedUserIds);
  }, [matrixClient, workspaceId, existingRoomMemberIds]);

  const workspaceMembers = useMemo(() => {
    if (!workspaceId || isLoadingWorkspaceMembers) return [];

    return getInviteableWorkspaceMembers({
      members: allWorkspaceMembers,
      myUserId: matrixClient.getUserId(),
      excludeUserIds: existingRoomMemberIds,
      isGuestServerMember: getIsFromGuestServer,
    }).map(
      (m) =>
        ({
          userId: m.id,
          rawDisplayName: m.name || m.id,
          name: m.name,
        }) as RoomMember
    );
  }, [allWorkspaceMembers, workspaceId, matrixClient, existingRoomMemberIds, isLoadingWorkspaceMembers]);

  const inviteesError = (errors as Record<string, unknown>).invitees;
  const firstErrorMessage = Array.isArray(inviteesError)
    ? inviteesError.find((error) => error && typeof error === 'object' && error.message)?.message
    : undefined;

  const toggleInvitee = (memberId: string) => {
    if (invitees.includes(memberId)) return removeInvitee(memberId);
    addInvitee(memberId);
  };

  const getDisplayName = (id: string) => {
    if (workspaceId) {
      const member = allWorkspaceMembers.find((m) => m.id === id);
      return member?.name ?? id;
    }

    const member = suggestedMembers.find((m) => m.userId === id);
    return member?.rawDisplayName ?? member?.name ?? id;
  };

  const foundMembers = !searchValue
    ? workspaceMembers
    : workspaceMembers.filter((member) => memberMatchesInviteSearch(member.rawDisplayName, member.userId, searchValue));

  const membersLabel = t(workspaceId ? 'messenger.workspace_members_label' : 'messenger.suggested_members_label');

  return (
    <div className="flex w-full gap-5 items-start">
      <UserPlus className="stroke-gray-60 h-6 w-6 shrink-0 stroke-1.5" />
      <div className="min-w-0 w-full flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="font-medium">{t('messenger.invite_members_label')}</p>
          {onCopyLink && (
            <button type="button" className="flex items-center gap-1 text-primary-50" onClick={onCopyLink}>
              <Link className="h-4 w-4 stroke-current stroke-1.5" />
              <span className="text-size-xs">{t('copy_link')}</span>
            </button>
          )}
        </div>
        {isPublic ? (
          <div className="flex flex-col">
            <InviteInputBase
              invitees={invitees}
              addInvitee={addInvitee}
              removeInvitee={removeInvitee}
              setInvitees={setInvitees}
              errorMessage={firstErrorMessage}
              placeholder={t('messenger.invite_members_input_placeholder')}
              rows={1}
              className="min-h-[48px]"
              getDisplayName={getDisplayName}
            />
            <span className="text-size-sm">{t('messenger.invite_members_helper')}</span>
          </div>
        ) : (
          <SearchInput
            variant="white-bg"
            reset={resetSearch}
            onChange={setSearchValue}
            onUnmount={resetSearch}
            inputClassName="!bg-gray-10"
            placeholder={t('messenger.search_members_placeholder')}
            size="sm"
            iconPlacement="left"
          />
        )}
        <InviteMembersFieldWrapper label={membersLabel}>
          {excludeRoomId && !excludeRoomMembersReady ? (
            <div className="flex flex-col items-center justify-center py-6 gap-2">
              <Spinner variant="gray50" size="xs" />
              <span className="text-size-sm text-gray-80">{t('loading')}</span>
            </div>
          ) : isLoadingWorkspaceMembers ? (
            <div className="flex flex-col items-center justify-center py-6 gap-2">
              <Spinner variant="gray50" size="xs" />
              <span className="text-size-sm text-gray-80">{t('loading')}</span>
            </div>
          ) : workspaceId ? (
            <WorkspaceMembersField members={foundMembers} invitees={invitees} toggleInvitee={toggleInvitee} />
          ) : (
            <SuggestedMembersField
              suggestedMembers={suggestedMembers}
              invitees={invitees}
              toggleInvitee={toggleInvitee}
            />
          )}
        </InviteMembersFieldWrapper>
      </div>
    </div>
  );
};

export default InviteMembersField;
